import { FormikProps } from "formik";
import React, { useEffect, useState } from "react";
import { BiPencil } from "react-icons/bi";
import Button from "../Button";
import Input from "../Input";
import PhoneInput from "../PhoneInput";
import Select from "../Select";
import { MainFormType, StepType } from "./CreateServiceModal";
import { getMaxDistance } from "../../apis/services/create-service";
import { getRemainingDeliveries } from "../../apis/services/get-remaining-deliveries";

interface RecipientDetailsProps {
  form: FormikProps<MainFormType>;
  onStepChange: (step: StepType) => void;
}

export const deliveryOptions = [
  {
    label: "Directly to recipient",
    value: "Direct",
  },
  {
    label: "In a secure mail room",
    value: "SecureMailRoom",
  },
  {
    label: "Leave at front door",
    value: "LeaveFrontDoor",
  },
  {
    label: "Doorman or front desk",
    value: "DoormanFrontDesk",
  },
  {
    label: "Other",
    value: "Other",
  },
];

const RecipientDetails = ({ form, onStepChange }: RecipientDetailsProps) => {
  const [isDriverFound, setIsDriverFound] = useState<null | boolean>(null);
  const [isFetchingMaxDistance, setIsFetchingMaxDistance] = useState(false);
  const [isFetchingDeliveries, setIsFetchingDeliveries] = useState(false)

  const handleGoToPickupLocation = () => {
    form.setFieldValue("step", "order");
    onStepChange("pickup");
  };

  const handleGoToDeliveryLocation = () => {
    form.setFieldValue("step", "order");
    onStepChange("dropoff");
  };

  const getDeliveryStatus = async () => {
    setIsFetchingDeliveries(true)
    const getDeliveryAvailabilityStatus = await getRemainingDeliveries(form?.values?.user?._id ? form?.values?.user?._id : "")
    if (getDeliveryAvailabilityStatus.success) {
      form.setFieldValue("recipientDetails.isDeliveriesAvailable", getDeliveryAvailabilityStatus?.data?.remainingDeliveriesStatus ? "1" : "0")
      setIsFetchingDeliveries(false)
    }
    else {

      setIsFetchingDeliveries(false)
    }
  }

  const handleRecipientDetailsSubmit = async () => {
    const detailErrors: any = {};
    const recipientDetails = form.values.recipientDetails;

    if (!recipientDetails.name) detailErrors.name = "This field is required";
    if (!recipientDetails.phone) detailErrors.phone = "This field is required";
    if (!recipientDetails.preferredDeliveryMethod)
      detailErrors.preferredDeliveryMethod =
        "Please select a pickup information";

    if (Object.keys(detailErrors).length === 0) {
      setIsFetchingMaxDistance(true);
      const maxDistance = await getMaxDistance(
        form.values.scheduleTime ? form.values.scheduleTime.toISOString() : ""
      );
      if (maxDistance?.success) {
        form.setFieldValue("maxDistance", maxDistance?.data);
        form.setFieldValue("step", "summary");
      }

    } else {
      form.setTouched({
        ...form.touched,
        recipientDetails: {
          ...form.touched.recipientDetails,
          name: true,
          preferredDeliveryMethod: true,
          phone: true,
        },
      });

      form.setErrors({
        ...form.errors,
        recipientDetails: {
          ...form.errors.recipientDetails,
          ...detailErrors,
        },
      });
    }
  };

  const recipientDetails = form.values.recipientDetails;
  const errors = form.errors.recipientDetails || {};
  const touched = form.touched.recipientDetails || {};

  const handleFieldChange = (field: string, value: string) => {
    form.setFieldValue(`recipientDetails.${field}`, value);
  };

  const handleFieldBlur = (field: string) => {
    form.setFieldTouched(`recipientDetails.${field}`, true);
  };

  useEffect(() => {
    getDeliveryStatus()
  }, [])

  return isDriverFound === null || isDriverFound === true ? (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleRecipientDetailsSubmit();
      }}
      className="flex flex-col gap-4"
    >
      <div className="relative">
        <p className="text-lg font-semibold text-center">Recipient details</p>
        <div className=" absolute top-1/2 md:-translate-y-1/2 translate-y-[40%] right-0">
          <Button
            type="button"
            onClick={() => {
              if (form.values?.user) {
                handleFieldChange("phone", form.values.user.phone);
                handleFieldChange("name", form.values.user.fullName);
              }
            }}
            className="w-full md:w-32 min-h-0 h-10 md:h-10 px-2"
          >
            Autofill
          </Button>
        </div>
      </div>
      <div className="md:grid md:grid-cols-2 flex flex-col gap-4">
        <Input
          label="Recipient name * "
          name="name"
          onBlur={() => handleFieldBlur("name")}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          value={recipientDetails.name}
          isInvalid={Boolean(touched.name && errors.name)}
          error={errors.name}
          autoFocus
          placeholder="Please enter recipient name"
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
        />
        <PhoneInput
          onChange={(e) => handleFieldChange("phone", e)}
          error={errors.phone}
          onBlur={() => handleFieldBlur("phone")}
          value={recipientDetails.phone}
          isInvalid={Boolean(touched.phone && errors.phone)}
          name="phone"
          label="Phone number *"
          responsiveFont={true}
        />
        <Input
          readOnly
          label="Pickup location * "
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
          placeholder="Please enter pickup location"
          value={
            form.values[form.values.serviceType!]?.pickupInformation?.address ||
            ""
          }
          rightContent={
            <div onClick={handleGoToPickupLocation}
              className="flex-shrink-0 border-l cursor-pointer border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black">
              <BiPencil className="text-primary" />
            </div>
          }
        />
        <Input
          label="Apartment number"
          name="aptNumber"
          onBlur={() => handleFieldBlur("aptNumber")}
          onChange={(e) => handleFieldChange("aptNumber", e.target.value)}
          value={recipientDetails.aptNumber}
          isInvalid={Boolean(touched.aptNumber && errors.aptNumber)}
          error={errors.aptNumber}
          placeholder="Please enter recipient apartment number"
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
        />
        <Input
          readOnly
          label="Delivery location * "
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
          placeholder="Please enter delivery location"
          value={
            form.values[form.values.serviceType!]?.dropoffInformation
              ?.address || ""
          }
          rightContent={
            <div onClick={handleGoToDeliveryLocation}
              className="flex-shrink-0 border-l cursor-pointer border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black">
              <BiPencil className="text-primary" />
            </div>
          }
        />
        <Input
          label="Delivery apartment number"
          name="dropoffAptNumber"
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
          onBlur={() => handleFieldBlur("dropoffAptNumber")}
          onChange={(e) =>
            handleFieldChange("dropoffAptNumber", e.target.value)
          }
          value={recipientDetails.dropoffAptNumber}
          isInvalid={Boolean(
            touched.dropoffAptNumber && errors.dropoffAptNumber
          )}
          error={errors.dropoffAptNumber}
          placeholder="Please enter delivery apartment number"
        />
        <Select
          placeholder="Select preferred delivery method"
          labelClassName='text-[1rem] lg:text-xs'
          onBlur={() => handleFieldBlur("preferredDeliveryMethod")}
          onChange={(e) =>
            handleFieldChange("preferredDeliveryMethod", e.target.value)
          }
          name="preferredDeliveryMethod"
          error={errors.preferredDeliveryMethod}
          isInvalid={Boolean(
            touched.preferredDeliveryMethod && errors.preferredDeliveryMethod
          )}
          value={recipientDetails.preferredDeliveryMethod}
          label="Preferred delivery method *"
          options={deliveryOptions}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              handleRecipientDetailsSubmit();
            }
          }}
          className='text-[1rem] lg:text-xs'
        />
        <Input
          label="Delivery instructions"
          name="deliveryInstructions"
          onBlur={() => handleFieldBlur("deliveryInstructions")}
          onChange={(e) =>
            handleFieldChange("deliveryInstructions", e.target.value)
          }
          value={recipientDetails.deliveryInstructions}
          isInvalid={Boolean(
            touched.deliveryInstructions && errors.deliveryInstructions
          )}
          error={errors.deliveryInstructions}
          placeholder="Please enter delivery instructions"
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
        />
      </div>
      <div className="flex justify-center mt-3 md:mr-3">
        <Button isLoading={isFetchingMaxDistance || isFetchingDeliveries} className="w-full md:w-32 min-h-0 h-10 md:h-10">Continue</Button>
      </div>
    </form>
  ) : (
    <div className="flex flex-col items-center my-10 gap-4">
      <p className="text-primary text-2xl font-bold">Sorry!</p>
      <p className="font-medium text-center">
        Unfortunately, we couldn't find a driver in your area right now.
      </p>
      <p className="text-sm">Please try again later.</p>
      <div className="flex justify-center mt-3 mr-3">
        <Button
          onClick={() => {
            setIsDriverFound(null);
          }}
          className="w-full md:w-32 min-h-0 h-10 md:h-10"
        >

          Go Back
        </Button>
      </div>
    </div>
  );
};

export default RecipientDetails;